/*
 * documentRevision — a monotonically increasing number that changes whenever the
 * document changes, INCLUDING changes the human made in the editor UI.
 *
 * Why not just count our own mutations: the whole product idea is a shared document.
 * If an agent caches "revision 7" and the human then drags a layer, the agent must be
 * able to tell that the world moved. miniPaint records its own UI edits in
 * `State.action_history`, so observing that history — plus a cheap structural
 * signature for edits that bypass it — catches human work too.
 *
 * `expectedRevision` on a write tool then means: "only apply this if nothing has
 * changed since I looked", which is what stops an agent from stomping a human edit.
 */

export class DocumentRevision {
  constructor () {
    this._revision = 0
    this._signature = null
    this._lastReason = 'initial'
  }

  get current () { return this._revision }
  get lastReason () { return this._lastReason }

  /** Record a change we made ourselves. */
  bump (reason = 'mutation') {
    this._revision += 1
    this._lastReason = reason
    return this._revision
  }

  /**
   * Detect changes made outside our tools (the human editing, or miniPaint's own
   * UI) and account for them. Call this at the start of every tool, read or write.
   *
   * @param {Window} win the miniPaint iframe window
   * @returns {number} the current revision
   */
  sync (win) {
    const signature = DocumentRevision.signature(win)
    if (this._signature === null) {
      this._signature = signature
      return this._revision
    }
    if (signature !== this._signature) {
      this._signature = signature
      this.bump('external')
    }
    return this._revision
  }

  /** Called after our own mutation so the external-change detector is not fooled by it. */
  commit (win, reason = 'mutation') {
    this.bump(reason)
    this._signature = DocumentRevision.signature(win)
    return this._revision
  }

  /**
   * A structural fingerprint of the document.
   *
   * Deliberately built from the *undo history position* plus canvas size and layer
   * identity — NOT from layer geometry. Every edit a human can make in miniPaint goes
   * through `State.do_action`, so it moves the history and is caught here.
   *
   * Geometry is excluded on purpose: a text layer with `boundary: 'dynamic'` has its
   * width and height recomputed during the next render, after the action that created
   * it has already completed. Including those made the detector fire "someone else
   * edited the document" moments after the agent's own write, which then rejected the
   * agent's next `expectedRevision` call. Derived layout churn is not an edit.
   */
  static signature (win) {
    const cfg = win.AppConfig
    const layers = win.Layers.get_layers()
    return [
      cfg.WIDTH,
      cfg.HEIGHT,
      win.State.action_history.length,
      win.State.action_history_index,
      layers.length,
      layers.map((l) => l.id).join(',')
    ].join('|')
  }
}

export const documentRevision = new DocumentRevision()
